import type {
  ForgeUsageSheet,
  UsageSheetLineStatus,
  UsageSheetPromiseMode,
  UsageSheetSourceKind,
} from '@shared/types/forge-usage-sheet';
import type { DesignBriefInputSource } from '@shared/types/tool-design';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const str = (value: unknown): string => (typeof value === 'string' ? value : '');

const list = (value: unknown): Record<string, unknown>[] =>
  Array.isArray(value) ? value.filter(isRecord) : [];

const oneOf = <T extends string>(value: unknown, allowed: readonly T[]): T | null =>
  typeof value === 'string' && (allowed as readonly string[]).includes(value) ? value as T : null;

const status = (value: unknown): UsageSheetLineStatus =>
  oneOf(value, ['confirmed', 'to_confirm'] as const) ?? 'confirmed';

/**
 * The usage sheet while Domino writes it (display only): every field may be
 * missing or cut, so lines appear once their identifying text is written and
 * unknown values stay empty instead of being guessed.
 */
export const toDraftUsageSheet = (value: unknown): ForgeUsageSheet | null => {
  if (!isRecord(value)) return null;
  const cap = isRecord(value.cap) ? value.cap : {};
  const job = isRecord(value.job) ? value.job : {};
  const usage = isRecord(value.usage) ? value.usage : {};
  const result = isRecord(value.result) ? value.result : {};
  return {
    title: str(value.title),
    cap: { problem: str(cap.problem), successCriterion: str(cap.successCriterion) },
    job: { who: str(job.who), moment: str(job.moment), nextDecision: str(job.nextDecision) },
    usage: {
      trigger: oneOf(usage.trigger, ['on_demand', 'schedule', 'event'] as const) ?? 'on_demand',
      rhythm: str(usage.rhythm),
      audience: oneOf(usage.audience, ['me', 'team'] as const) ?? 'me',
    },
    inputs: list(value.inputs).flatMap((input) => {
      const source = oneOf<DesignBriefInputSource>(input.source, ['user', 'profile', 'crm', 'default']);
      return str(input.label) && source
        ? [{ label: str(input.label), source, proposal: str(input.proposal), why: str(input.why), status: status(input.status) }]
        : [];
    }),
    result: {
      form: str(result.form),
      afterwards: str(result.afterwards),
      outputs: list(result.outputs).flatMap((output) => str(output.label)
        ? [{ label: str(output.label), why: str(output.why), status: status(output.status) }]
        : []),
    },
    sources: list(value.sources).flatMap((source) => {
      const kind = oneOf<UsageSheetSourceKind>(source.kind, ['connected', 'to_connect', 'web', 'document', 'profile', 'crm']);
      return str(source.label) && kind
        ? [{ label: str(source.label), kind, why: str(source.why), status: status(source.status) }]
        : [];
    }),
    promises: list(value.promises).flatMap((promise) => {
      const mode = oneOf<UsageSheetPromiseMode>(promise.mode, ['domino', 'with_you', 'not_yet']);
      return str(promise.promise) && mode ? [{ promise: str(promise.promise), mode, detail: str(promise.detail) }] : [];
    }),
    ideas: list(value.ideas).flatMap((idea) => str(idea.idea) ? [{ idea: str(idea.idea), why: str(idea.why) }] : []),
    doubts: list(value.doubts).flatMap((doubt) => str(doubt.doubt)
      ? [{
        doubt: str(doubt.doubt),
        consequence: str(doubt.consequence),
        proposal: str(doubt.proposal),
        needsDecision: doubt.needsDecision === true,
      }]
      : []),
  };
};
